import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Boxes, FolderOpen, ImageIcon, LayoutGrid, Megaphone, Search, UserRound, Video, Wand2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { TOOLS, matches, toolOpen, useGenerations, useMediaConfig, usePresets } from "@/lib/studio";

const TOOL_ICON = { "image-create": ImageIcon, "image-edit": Wand2, video: Video, assets: FolderOpen, presets: LayoutGrid, influencer: UserRound, ads: Megaphone, models: Boxes };

function Group({ title, children, testId }) {
  return (
    <section className="space-y-1.5" data-testid={testId}>
      <h3 className="px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{title}</h3>
      {children}
    </section>
  );
}

function Row({ thumb, icon: Icon, title, hint, badge, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-white/[0.06] focus-visible:bg-white/[0.08] focus-visible:outline-none">
      {thumb ? <img src={thumb} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" /> : (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-zinc-300"><Icon className="h-4 w-4" /></span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-zinc-100">{title}</span>
        {hint && <span className="block truncate text-xs text-zinc-500">{hint}</span>}
      </span>
      {badge && <span className="rounded-full bg-white/[0.08] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{badge}</span>}
      <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-600" />
    </button>
  );
}

function useResults(query) {
  const config = useMediaConfig().data;
  const presetData = usePresets().data;
  const genData = useGenerations().data;
  return useMemo(() => {
    const presets = presetData?.items || [];
    const creations = genData?.items || [];
    const q = query.trim().toLowerCase();
    const tools = TOOLS.filter((t) => !q || matches(q, t.label, t.hint)).map((t) => ({ ...t, open: toolOpen(t, config) }));
    const ps = presets.filter((p) => p.status === "published" && (!q || matches(q, p.title, p.description, p.category, p.prompt, p.kind))).slice(0, q ? 12 : 6);
    const cs = creations.filter((g) => g.status === "complete" && (!q || matches(q, g.prompt))).slice(0, q ? 12 : 4);
    return { q, tools, presets: ps, creations: cs };
  }, [query, config, presetData, genData]);
}

export function SearchOverlay({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const r = useResults(query);
  const go = (fn) => { onOpenChange(false); setQuery(""); fn(); };
  const empty = r.q && !r.tools.length && !r.presets.length && !r.creations.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="studio-search-overlay"
        className="left-0 top-0 flex h-dvh max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 bg-ink-950/95 p-0 backdrop-blur-xl sm:left-1/2 sm:top-[10vh] sm:h-auto sm:max-h-[78vh] sm:max-w-2xl sm:-translate-x-1/2 sm:rounded-3xl sm:border sm:border-white/10 [&>button.absolute]:hidden">
        <DialogTitle className="sr-only">Search Create Studio</DialogTitle>
        <div className="flex items-center gap-3 border-b border-white/[0.08] px-4 py-3" style={{ paddingTop: "max(env(safe-area-inset-top), 12px)" }}>
          <Search className="h-5 w-5 shrink-0 text-zinc-500" />
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search presets, tools and your creations" aria-label="Search"
            data-testid="search-overlay-input" className="h-11 min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-zinc-500 focus:outline-none" />
          <button type="button" onClick={() => onOpenChange(false)} aria-label="Close search" data-testid="search-overlay-close"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.06] text-zinc-300 hover:bg-white/[0.12] hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-4 sm:px-4">
          {empty && <p className="px-1 py-10 text-center text-sm text-zinc-500" data-testid="search-empty">No matches for “{query.trim()}”. Try a style, a place or a product.</p>}
          {r.tools.length > 0 && (
            <Group title="Tools" testId="search-group-tools">
              {r.tools.map((t) => <Row key={t.key} icon={TOOL_ICON[t.key]} title={t.label} hint={t.hint} badge={t.open ? null : "Soon"} testId={`search-tool-${t.key}`} onClick={() => go(() => navigate(t.to))} />)}
            </Group>
          )}
          {r.presets.length > 0 && (
            <Group title={r.q ? "Presets" : "Popular presets"} testId="search-group-presets">
              {r.presets.map((p) => (
                <Row key={p.id} thumb={p.cover_url} title={p.title} hint={`${p.category} · ${p.kind === "video" ? "Video, coming soon" : p.operation === "edit" ? "Uses your photo" : "Image"}`}
                  testId={`search-preset-${p.slug || p.id}`} onClick={() => go(() => setParams((prev) => { prev.set("preset", p.id); return prev; }))} />
              ))}
            </Group>
          )}
          {r.creations.length > 0 && (
            <Group title={r.q ? "Your creations" : "Recent creations"} testId="search-group-creations">
              {r.creations.map((g) => <Row key={g.id} thumb={g.thumb_url} title={g.prompt} hint={g.operation === "edit" ? "Edited image" : "Image"} testId={`search-creation-${g.id}`} onClick={() => go(() => navigate("/create/assets"))} />)}
            </Group>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
